import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';
import {
  companyActivateOrInactivate,
  createCompany,
  deleteCompany,
  getCompany,
  listCompaniesByEmployee,
  listCompaniesLinkedEmployee,
  updateCompany,
  getCompanySettings,
  createCompanySettings,
  updateCompanySettings,
  listCompaniesReduced,
  getCompanyReduced,
  getCompanyProvider,
  deleteCompanyContact,
  updateLogoCompany,
  deleteLogoCompany,
} from './Companies.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { getUserFromLocalStorage } from '@/utils/user';
import {
  CompanyCreateRequest,
  CompanyReduced,
  CompanySettingsInterface,
  CompanyUpdateRequest,
  ListCompaniesQuery,
  ListCompaniesQueryReduced,
} from './Companies.types';
import { useIntl } from 'react-intl';
import { message, notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { CompaniesErrors } from './Companies.errors';
import { Omit } from 'lodash';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useCompanies(params?: ListCompaniesQuery) {
  const query = useInfiniteQuery(
    ['companies', { ...params }],
    async ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return await listCompaniesLinkedEmployee(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCompaniesReduced(
  params?: ListCompaniesQueryReduced,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['companies-reduced', { ...params }]);

  return useInfiniteQuery(
    ['companies-reduced', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listCompaniesReduced(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCompanyCreate() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const query = useMutation((obj: CompanyCreateRequest) => createCompany(obj), {
    onSuccess() {
      queryClient.resetQueries(['companies-reduced']);

      notification.success({
        message: intl.formatMessage({
          id: 'companies.create.notification.success',
        }),
        description: intl.formatMessage({
          id: 'companies.create.notification.successDescription',
        }),
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes(
              'ERROR: duplicate key value violates unique constraint "tb_company_name_matrix_id_key"\n',
            )
              ? 'Já existe nessa matriz uma empresa cadastrada com esse nome.'
              : err,
          ),
      });
    },
  });

  return query;
}

export function useCompaniesByLoggedEmployee(
  params?: PageRequest,
  enabled = true,
) {
  const query = useInfiniteQuery(
    ['companies/employee', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      const user = getUserFromLocalStorage();

      return listCompaniesByEmployee(
        user.employeeId ? +user.employeeId : 0,
        req,
      );
    },
    {
      getNextPageParam: (lastPage) => lastPage?.nextToken,
      enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCompany(id?: number, enabled = true) {
  return useQuery(['companies', id], () => getCompany(id), {
    enabled: !!id && enabled,
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCompanyMutation() {
  return useMutation(getCompany, {
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCompanyReduced() {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<CompanyReduced>([
        'company-detail-reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getCompanyReduced(id);
      return data;
    },
    {
      onError(error) {
        notificationError(error);
      },
      onSuccess(data, id) {
        queryClient.setQueryData(['company-detail-reduced', id], data);
      },
    },
  );
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const mutation = useMutation((id: number) => deleteCompany(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'companies.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'companies.remove.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries(['companies']);
      queryClient.resetQueries(['companies-reduced']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => CompaniesErrors[err] || err),
      });
    },
  });

  return mutation;
}

export function useActiveOrInactivateCompany() {
  const intl = useIntl();

  return useMutation(
    (companyId: number) => companyActivateOrInactivate(companyId),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({ id: 'companies.status.message.success' }),
        );
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useUpdateCompany() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: CompanyUpdateRequest) => updateCompany(obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(['companies']);
        queryClient.resetQueries(['companies-reduced']);
        notification.success({
          message: intl({ id: 'companies.update.notification.success' }),
          description: intl({
            id: 'companies.update.notification.successDescription',
          }),
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes(
                'ERROR: duplicate key value violates unique constraint "tb_company_name_matrix_id_key"',
              )
                ? 'Já existe nessa matriz uma empresa cadastrada com esse nome.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useUpdateLogoCompany() {
  const queryChanel = useQueryChanel();

  return useMutation(updateLogoCompany, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'Logo da empresa atualizada',
      });

      queryChanel({
        invalidateQueries: [['companies']],
        resetQueries: [['companies-reduced']],
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useDeleteLogoCompany() {
  const queryChanel = useQueryChanel();

  return useMutation(deleteLogoCompany, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'Logo da empresa removida',
      });

      queryChanel({
        invalidateQueries: [['companies']],
        resetQueries: [['companies-reduced']],
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useGetCompanyProvider(companyId?: number) {
  return useQuery(
    ['companyProvider', companyId],
    () => getCompanyProvider(companyId),
    {
      enabled: !!companyId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCompanySettings(companyId?: number) {
  return useQuery(
    ['companySettings', companyId],
    () => getCompanySettings(companyId),
    {
      enabled: !!companyId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCreateCompanySettings(successMessage?: string) {
  const queryChanel = useQueryChanel();

  return useMutation(
    ({ ...params }: Omit<CompanySettingsInterface, 'id'>) =>
      createCompanySettings(params),
    {
      onSuccess(_, variables) {
        notification.success({
          message: 'Salvo com sucesso!',
          description:
            successMessage ??
            'A configuração do financeiro foi criada com sucesso',
        });

        queryChanel({
          invalidateQueries: [['companySettings', variables.companyId]],
        });
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useUpdateCompanySettings(successMessage?: string) {
  const queryChanel = useQueryChanel();

  return useMutation(
    ({ companyId, ...params }: CompanySettingsInterface) =>
      updateCompanySettings(companyId, params),
    {
      onSuccess(_, variables) {
        notification.success({
          message: 'Salvo com sucesso!',
          description:
            successMessage ??
            'A configuração do financeiro foi alterada com sucesso',
        });

        queryChanel({
          invalidateQueries: [['companySettings', variables.companyId]],
        });
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useDeleteCompanyContact() {
  return useMutation(deleteCompanyContact, {
    onError(error) {
      notificationError(error);
    },
  });
}
